import { useEffect, useState, useMemo, useRef } from "react";
import Highcharts from "highcharts";
import { ToggleButtonGroup, ToggleButton, TextField, Popover } from '@mui/material';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { DateRangePicker } from 'react-date-range';
import { formatDateDisplayNoYear } from "../../utils/date";

const ResultComponent = ({
  current,
  historical,
  shortTerm,
  xAxis,
  id,
  name,
  unit,
}) => {
  const [selectedTypes, setSelectedTypes] = useState(['current', 'historical', 'short_term']);
  const [minYRange, setMinYRange] = useState();
  const [maxYRange, setMaxYRange] = useState();
  const [minXRange, setMinXRange] = useState(null);
  const [maxXRange, setMaxXRange] = useState(null);
  const [pickerAnchor, setPickerAnchor] = useState(null);

  const chartRef = useRef(null);

  const categories = useMemo(() => {
    console.log("CURRENT:", current)
    if (!current?.points) return [];
    return xAxis;
  }, [current]);

  const xAxisDateRange = useMemo(() => {
    if (!categories || categories.length === 0)
      return {
        minDate: new Date(`2024-01-01T00:00:00`),
        maxDate: new Date(`2024-12-31T23:59:59`),
      };
 
    const minDate = new Date(`2024-${categories[0]}T00:00:00`);
    const maxDate = new Date(`2024-${categories[categories.length - 1]}T23:59:59`);
    return { minDate, maxDate };
  }, [categories]);

  const [dateRange, setDateRange] = useState([
    {
      startDate: xAxisDateRange.minDate,
      endDate: xAxisDateRange.maxDate,
      key: 'selection'
    }
  ]);

  useEffect(() => {
    setDateRange([
      {
        startDate: xAxisDateRange.minDate,
        endDate: xAxisDateRange.maxDate,
        key: 'selection'
      }
    ]);
  }, [xAxisDateRange]);

  const calculateStats = (data) => {
    if (!data?.points) return [];

    return data.points.map(timePoint => ({
      date: timePoint.date,
      time: timePoint.time,
      min: timePoint.min,
      max: timePoint.max,
      mean: timePoint.mean,
      median: timePoint.median,
      point_start: data.point_start,
      // Arrays for tooltip
      dates: timePoint.dates || null,
      mins: timePoint.mins || null,
      maxs: timePoint.maxs || null,
      means: timePoint.means || null,
      medians: timePoint.medians || null
    }));
  };

  const currentStats = useMemo(() => calculateStats(current), [current]);
  const historicalStats = useMemo(() => calculateStats(historical), [historical]);
  const shortTermStats = useMemo(() => calculateStats(shortTerm), [shortTerm]);

  console.log("currentStats:", currentStats)

  const series = useMemo(() => {
    const seriesData = [];
    const addSeries = (stats, namePrefix) => {
      const statTypes = [
        { key: 'mean', arrayKey: 'means', name: 'Mean', symbol: 'circle' },
        { key: 'max', arrayKey: 'maxs', name: 'Max', symbol: 'diamond' },
        { key: 'min', arrayKey: 'mins', name: 'Min', symbol: 'triangle-down' },
        { key: 'median', arrayKey: 'medians', name: 'Median', symbol: 'square' }
      ];

      const pointStart = stats[0]?.point_start || 0;

      statTypes.forEach(({ key, arrayKey, name, symbol }) => {
        const data = stats.map(timePoint => {

          console.log("NAME:", namePrefix, "TIMEPOINT:", timePoint, "Array Key:", arrayKey, "BOTH:", timePoint[arrayKey], timePoint[key])
          // Check if the arrays are null
          const hasNullArrays = !timePoint[arrayKey] && timePoint[key] === 0;

          console.log("NULL:", hasNullArrays);

          return {
            y: timePoint[key],
            date: timePoint.date,
            time: timePoint.time,
            dates: timePoint.dates,
            detailedValues: timePoint[arrayKey],
            marker: hasNullArrays ? {
              enabled: false,
              symbol: symbol
            } : {
              enabled: true,
              symbol: symbol
            },
            enableMouseTracking: !hasNullArrays
          };
        });

        seriesData.push({
          name: `${namePrefix} ${name}`,
          data,
          pointStart: pointStart
        });
      });
    };

    selectedTypes.forEach(type => {
      if (type === 'current' && currentStats.length) addSeries(currentStats, 'Current');
      if (type === 'historical' && historicalStats.length) addSeries(historicalStats, 'Historical');
      if (type === 'short_term' && shortTermStats.length) addSeries(shortTermStats, 'Short Term');
    });

    return seriesData;
  }, [selectedTypes, currentStats, historicalStats, shortTermStats]);

  const chartOptions = useMemo(() => ({
    chart: { type: 'spline' },
    title: { text: `${name} Monitoring` },
    xAxis: {
      categories,
      tickInterval: Math.ceil(categories.length / 10), // Show fewer ticks for better readability
      labels: {
        rotation: -45,
        align: 'right',
        style: {
          fontSize: '11px'
        }
      }
    },
    yAxis: {
      title: { text: `${name} ${unit ? unit : ""}` }
    },
    plotOptions: {
      series: {
        pointStart: null,
        connectNulls: true,
        marker: {
          enabled: true
        }
      }
    },
    tooltip: {
      useHTML: true,
      formatter: function () {
        if (!this.point.dates?.length) {
          return `<b>${this.x}</b><br>${this.series.name}: ${this.y.toFixed(2)}`;
        }

        let html = `
        <table style="border-collapse: collapse;">
        <tr>
              <td style="padding: 2px 5px;"><b>${this.x}</b></td>
              <td style="padding: 2px 5px;"><b>${this.y.toFixed(2)}</b></td>
            </tr>
            </table>`;
        html += `<br><b>${this.series.name}</b><br/>`;
        html += '<table style="border-collapse: collapse;">';
        html += '<tr><th style="padding: 2px 5px;">Date</th><th style="padding: 2px 5px;">Value</th></tr>';

        this.point.dates.forEach((date, i) => {
          const value = this.point.detailedValues[i];
          if (value !== undefined) {
            html += `<tr>
              <td style="padding: 2px 5px;">${date}</td>
              <td style="padding: 2px 5px;">${value.toFixed(2)}</td>
            </tr>`;
          }
        });

        html += '</table>';
        return html;
      }
    },
    series
  }), [name, unit, categories, series]);

  // Create chart once when id changes (mount/unmount)
  useEffect(() => {
    const chart = Highcharts.chart(id, chartOptions);
    chartRef.current = chart;
    return () => {
      chart.destroy();
      chartRef.current = null;
    };
  }, [id]);

  // Update existing chart when options change (no destroy/recreate)
  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.update(chartOptions, true, true);
    }
  }, [chartOptions]);

  const updateYAxis = () => {
    const min = parseFloat(minYRange);
    const max = parseFloat(maxYRange);
    if (!isNaN(min) && !isNaN(max)) {
      chartRef.current?.yAxis[0].update({ min, max }, true);
    }
  };

  const updateXAxis = () => {
    if (!minXRange || !maxXRange) return;

    const minIndex = categories.findIndex(date => date === minXRange);
    const maxIndex = categories.findIndex(date => date === maxXRange);

    if (minIndex !== -1 && maxIndex !== -1) {
      chartRef.current?.xAxis[0].update({ min: minIndex, max: maxIndex }, true);
    }
  };

  return (
    <div className="chart-container" style={{
      border: "1px solid",
      margin: "5em 5em 0 5em",
    }}>
      <div className="display-type-buttons">
        <ToggleButtonGroup
          className="toggle-button-group"
          value={selectedTypes}
          onChange={(_, newTypes) => setSelectedTypes(newTypes.length ? newTypes : selectedTypes)}
          aria-label="data type selection"
        >
          {['current', 'historical', 'short_term'].map(type => (
            <ToggleButton className="toggle-button" key={type} value={type}>
              {type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </div>
      <div className="x-y-axis-container">
        <div className="y-axis-config">
          <div className="y-axis-inputs">
            <input
              className="y-axis-input"
              type="number"
              placeholder="Min"
              onChange={e => setMinYRange(e.target.value)}
            />
            <input
              type="number"
              placeholder="Max"
              onChange={e => setMaxYRange(e.target.value)}
              className="y-axis-input"
            />
            <button className="y-axis-confirm" onClick={updateYAxis}>Set Y Axis Range</button>
          </div>
        </div>

        <div className="x-axis-config">
          <div className="x-axis-inputs">
            <TextField
              variant="outlined"
              fullWidth
              size="small"
              value={
                minXRange && maxXRange
                  ? `${formatDateDisplayNoYear(new Date(`2024-${minXRange}T00:00:00`))} - ${formatDateDisplayNoYear(new Date(`2024-${maxXRange}T00:00:00`))}`
                  : 'Select date range'
              }
              onClick={(event) => setPickerAnchor(event.currentTarget)}
              InputProps={{ readOnly: true }}
              className="x-axis-input"
              style={{ width: "100%" }}
            />
            <Popover
              open={Boolean(pickerAnchor)}
              anchorEl={pickerAnchor}
              onClose={() => setPickerAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <DateRangePicker
                ranges={dateRange}
                onChange={(ranges) => {
                  setDateRange([ranges.selection]);
                  const formatDate = (d) => {
                    const month = (d.getMonth() + 1).toString().padStart(2, '0');
                    const day = d.getDate().toString().padStart(2, '0');
                    return `${month}-${day}`;
                  };
                  setMinXRange(formatDate(ranges.selection.startDate));
                  setMaxXRange(formatDate(ranges.selection.endDate));
                }}
                minDate={new Date('2024-01-01')}
                maxDate={new Date('2024-12-31')}
                disabledDay={(date) => (
                  date < xAxisDateRange.minDate || date > xAxisDateRange.maxDate
                )}
                months={1}
                direction="horizontal"
                showMonthAndYearPickers={false}
                staticRanges={[]}
                inputRanges={[]}
                dateDisplayFormat="dd/MM"
                monthDisplayFormat="MMMM"
                showDateDisplay={true}
                moveRangeOnFirstSelection={false}
                retainEndDateOnFirstSelection={true}
                navigatorRenderer={(focusedDate, changeShownDate, props) => (
                  <div onMouseUp={e => e.stopPropagation()} className="rdrMonthAndYearWrapper">
                    {props.showMonthArrow !== false && (
                      <button
                        type="button"
                        className="rdrNextPrevButton rdrPprevButton"
                        onClick={() => changeShownDate(-1, 'monthOffset')}
                        aria-label={props.ariaLabels?.prevButton}
                      >
                        <i />
                      </button>
                    )}
                    <span className="rdrMonthAndYearPickers">
                      {focusedDate.toLocaleString('default', { month: 'long' })}
                    </span>
                    {props.showMonthArrow !== false && (
                      <button
                        type="button"
                        className="rdrNextPrevButton rdrNextButton"
                        onClick={() => changeShownDate(+1, 'monthOffset')}
                        aria-label={props.ariaLabels?.nextButton}
                      >
                        <i />
                      </button>
                    )}
                  </div>
                )}
              />
            </Popover>
            <button onClick={updateXAxis} className="x-axis-confirm">Set Date Range</button>
            <button
              onClick={() => {
                setDateRange([
                  {
                    startDate: xAxisDateRange.minDate,
                    endDate: xAxisDateRange.maxDate,
                    key: 'selection'
                  }
                ]);
                setMinXRange(null);
                setMaxXRange(null);
                chartRef.current?.xAxis[0].update({
                  min: null,
                  max: null
                }, true);
              }}
              className="x-axis-confirm"
            >
              Clear
            </button>
          </div>
        </div>
      </div>


      <div id={id} />
    </div>
  );
};

export default ResultComponent;
